/**
 * PEÇAS DESMEMBRADAS do ambiente (v3).
 *
 * Pedido do dono: dentro do ambiente o gestor desmembra uma peça escrevendo o
 * nome dela ("porta do armário") e ela vira um DERIVADO EXTERNO do ambiente,
 * com vida própria — anda pelos setores, tem status, responsável, prazos e
 * tarefas. Nasce HERDANDO prioridade e prazos do ambiente que a originou (e o
 * gestor pode alterar depois), pode RETORNAR ao ambiente ("voltou para o
 * ambiente") ou ser concluída sem retorno. O ambiente mostra quantas peças
 * estão fora.
 *
 * A peça vive no backend (`/api/mapa-producao/pecas`) e se liga ao ambiente
 * pela mesma CHAVE em texto das tarefas e das alocações (ADR-0075): nada aqui
 * aponta para pedido/produção do ERP. O ambiente de origem pode não estar
 * carregado (linha da planilha que saiu do mapa porque foi carregada) — nesse
 * caso a peça continua existindo, com `ambiente: null` e o rótulo gravado.
 *
 * Módulo puro: nada de DOM, nada de rede.
 */
import { isBeforeToday, toDate } from "../../../utils/status";
import {
  ORIGEM_PRIORIDADE,
  PRIORIDADES,
  ROTULO_STATUS_PECA,
  STATUS_PECA,
  TOM_STATUS_PECA,
  pecaFora,
  prioridadeDerivada,
  tarefaAberta,
} from "./vocabulario";

const LISTA_VAZIA = Object.freeze([]);
const SEM_TEXTO = "";

/** Origem do prazo/prioridade da peça, para o rótulo dizer a régua (ADR-0059). */
export const ORIGEM_PECA = Object.freeze({
  /** Alguém digitou na peça. */
  PECA: "peca",
  /** Herdado do ambiente que a originou. */
  AMBIENTE: "ambiente",
  /** Deduzido do prazo (nem a peça nem o ambiente tinham). */
  PRAZO: "prazo",
});

const dois = (numero) => String(numero).padStart(2, "0");

/**
 * "AAAA-MM-DD" a partir de uma Date, em hora LOCAL — `toISOString()` daria o
 * dia anterior em São Paulo (ADR-0059). É o formato que o backend aceita nos
 * campos de prazo.
 */
export function paraDataIso(valor) {
  const data = toDate(valor);
  if (!data) return null;
  return `${data.getFullYear()}-${dois(data.getMonth() + 1)}-${dois(data.getDate())}`;
}

/** "PC-12 · Porta do armário" — o que a tela e os eventos mostram. */
export function rotuloPeca(peca) {
  return [peca?.codigo, peca?.nome].filter(Boolean).join(" · ");
}

/**
 * Quantas peças estão FORA de cada ambiente: Map<ambiente_chave, número>.
 * Peça retornada, concluída ou cancelada não conta — ela não está mais fora.
 */
export function contarPecasForaPorAmbiente(pecas) {
  const contagem = new Map();
  (pecas || []).forEach((peca) => {
    if (!peca?.ambiente_chave || !pecaFora(peca)) return;
    contagem.set(peca.ambiente_chave, (contagem.get(peca.ambiente_chave) || 0) + 1);
  });
  return contagem;
}

/**
 * Valores que o formulário de "desmembrar peça" já vem preenchido: a peça nasce
 * com a prioridade e os prazos do ambiente. Devolve exatamente o formato do
 * corpo do POST (`{prioridade, data_prevista, data_limite}`, datas em
 * "AAAA-MM-DD"), para a tela não precisar converter nada.
 *
 * Sem ambiente (ou ambiente sem prazo), prioridade normal e datas nulas — o
 * gestor digita. Nunca inventa data.
 */
export function herdarDoAmbiente(demanda) {
  const prioridade = PRIORIDADES.includes(demanda?.prioridade)
    ? demanda.prioridade
    : prioridadeDerivada(demanda?.prazoEfetivo || demanda?.prazo);
  return {
    prioridade,
    data_prevista: paraDataIso(demanda?.prevista),
    data_limite: paraDataIso(demanda?.prazoEfetivo || demanda?.prazo),
  };
}

function prazoDaPeca(peca, ambiente) {
  const daPeca = toDate(peca.data_limite);
  if (daPeca) return { prazoEfetivo: daPeca, origemPrazo: ORIGEM_PECA.PECA };
  const doAmbiente = ambiente?.prazoEfetivo || ambiente?.prazo || null;
  if (doAmbiente) return { prazoEfetivo: doAmbiente, origemPrazo: ORIGEM_PECA.AMBIENTE };
  return { prazoEfetivo: null, origemPrazo: null };
}

function prioridadeDaPeca(peca, ambiente, prazoEfetivo, hoje) {
  if (PRIORIDADES.includes(peca.prioridade)) {
    return { prioridade: peca.prioridade, origemPrioridade: ORIGEM_PECA.PECA };
  }
  if (PRIORIDADES.includes(ambiente?.prioridade) && ambiente?.origemPrioridade === ORIGEM_PRIORIDADE.APP) {
    return { prioridade: ambiente.prioridade, origemPrioridade: ORIGEM_PECA.AMBIENTE };
  }
  return { prioridade: prioridadeDerivada(prazoEfetivo, hoje), origemPrioridade: ORIGEM_PECA.PRAZO };
}

function combinarUma(peca, { ambiente, setor, tarefas, hoje }) {
  const { prazoEfetivo, origemPrazo } = prazoDaPeca(peca, ambiente);
  const { prioridade, origemPrioridade } = prioridadeDaPeca(peca, ambiente, prazoEfetivo, hoje);
  const fora = pecaFora(peca);
  return {
    ...peca,
    id: peca.id,
    codigo: peca.codigo || SEM_TEXTO,
    nome: peca.nome || SEM_TEXTO,
    rotulo: rotuloPeca(peca),
    ambienteChave: peca.ambiente_chave || null,
    ambienteRotulo: ambiente?.rotulo || peca.ambiente_rotulo || SEM_TEXTO,
    ambiente: ambiente || null,
    cliente: ambiente?.cliente || SEM_TEXTO,
    lote: ambiente?.lote || SEM_TEXTO,
    setorId: peca.setor_id || null,
    setorNome: setor?.nome || SEM_TEXTO,
    subsetorId: peca.subsetor_id || null,
    postoId: peca.posto_id || null,
    colaboradorId: peca.colaborador_id || null,
    status: peca.status || STATUS_PECA.AGUARDANDO,
    rotuloStatus: ROTULO_STATUS_PECA[peca.status] || SEM_TEXTO,
    tomStatus: TOM_STATUS_PECA[peca.status] || "neutral",
    fora,
    prevista: toDate(peca.data_prevista),
    prazoEfetivo,
    origemPrazo,
    prioridade,
    origemPrioridade,
    // Só a peça fora do ambiente pode estar atrasada: peça retornada já
    // cumpriu o papel dela, e pintar de vermelho o que acabou é ruído.
    atrasada: fora && Boolean(prazoEfetivo) && isBeforeToday(prazoEfetivo, hoje),
    tarefas,
    versao: peca.versao ?? 0,
  };
}

const PESO_PRIORIDADE = { urgente: 0, alta: 1, normal: 2 };

function ordenarPecas(a, b) {
  if (a.fora !== b.fora) return a.fora ? -1 : 1;
  if (a.atrasada !== b.atrasada) return a.atrasada ? -1 : 1;
  const peso = (PESO_PRIORIDADE[a.prioridade] ?? 9) - (PESO_PRIORIDADE[b.prioridade] ?? 9);
  if (peso) return peso;
  if (a.prazoEfetivo && b.prazoEfetivo && a.prazoEfetivo - b.prazoEfetivo) return a.prazoEfetivo - b.prazoEfetivo;
  if (Boolean(a.prazoEfetivo) !== Boolean(b.prazoEfetivo)) return a.prazoEfetivo ? -1 : 1;
  return String(a.codigo).localeCompare(String(b.codigo), "pt-BR", { numeric: true });
}

function tarefasPorPeca(tarefas) {
  const mapa = new Map();
  (tarefas || []).filter(tarefaAberta).forEach((tarefa) => {
    if (!tarefa.peca_id) return;
    if (!mapa.has(tarefa.peca_id)) mapa.set(tarefa.peca_id, []);
    mapa.get(tarefa.peca_id).push(tarefa);
  });
  return mapa;
}

function ambientesPorChave(demandas) {
  const mapa = new Map();
  (demandas || []).forEach((demanda) => {
    if (demanda?.chave && !mapa.has(demanda.chave)) mapa.set(demanda.chave, demanda);
  });
  return mapa;
}

/**
 * As peças do `estado` (GET /estado ou /pecas) enriquecidas com o ambiente de
 * origem, o nome do setor e as tarefas abertas. `demandas` = as combinadas
 * (`combinarDemandas`); `opcoes` = {catalogo, hoje}.
 *
 * Cada peça: {...campos do backend, codigo, nome, rotulo, ambienteChave,
 * ambienteRotulo, ambiente, cliente, lote, setorId, setorNome, subsetorId,
 * postoId, colaboradorId, status, rotuloStatus, tomStatus, fora, prevista,
 * prazoEfetivo, origemPrazo, prioridade, origemPrioridade, atrasada, tarefas,
 * versao}.
 *
 * Ordem: as que estão fora primeiro, atrasadas na frente, depois por
 * prioridade, prazo e código.
 */
export function combinarPecas(estado, demandas = [], { catalogo = null, hoje = new Date() } = {}) {
  const pecas = Array.isArray(estado?.pecas) ? estado.pecas : [];
  if (pecas.length === 0) return [];
  const porChave = ambientesPorChave(demandas);
  const porPeca = tarefasPorPeca(estado?.tarefas);
  return pecas
    .filter((peca) => peca && peca.id)
    .map((peca) =>
      combinarUma(peca, {
        ambiente: peca.ambiente_chave ? porChave.get(peca.ambiente_chave) || null : null,
        setor: catalogo?.setor(peca.setor_id) || null,
        tarefas: porPeca.get(peca.id) || LISTA_VAZIA,
        hoje,
      })
    )
    .sort(ordenarPecas);
}

/** As peças de um ambiente (combinadas), na mesma ordem. */
export function pecasDoAmbiente(pecasCombinadas, ambienteChave) {
  if (!ambienteChave) return LISTA_VAZIA;
  return (pecasCombinadas || []).filter((peca) => peca.ambienteChave === ambienteChave);
}

/** As peças fora do ambiente (é a lista que o selo "N peças fora" abre). */
export function pecasForaDoAmbiente(pecasCombinadas, ambienteChave) {
  return pecasDoAmbiente(pecasCombinadas, ambienteChave).filter((peca) => peca.fora);
}
